import { useSelector } from 'react-redux';
import { queryParamKeys } from 'modules/experiments/constants';
import { getExperimentMetricsOptionAndDefault } from 'modules/experiments/ducks/selectors';
import { DateRange, EXPERIMENT_ANALYTICS_TAB } from 'modules/experiments/types';
import {
  createSerializationOptionsForEnumValue,
  serializationOptionsForDateTypeValue,
  serializationOptionsForNumberValue,
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { useProjectsTimeZone } from './useProjectsTimeZone';

export const useExperimentSearchParams = () => {
  const defaultTimeZone = useProjectsTimeZone();
  const { defaultValue: defaultPerformanceOverTimeMetric } = useSelector(
    getExperimentMetricsOptionAndDefault,
  );

  const [experimentId] = useSearchParamState({
    searchParamKey: queryParamKeys.experimentId,
    stateValueWhenSearchParamIsUnset: 0,
    ...serializationOptionsForNumberValue,
  });

  const [selectedTab, setSelectedTab] = useSearchParamState({
    searchParamKey: queryParamKeys.tab,
    stateValueWhenSearchParamIsUnset: EXPERIMENT_ANALYTICS_TAB.OVERVIEW,
    ...createSerializationOptionsForEnumValue({
      enumKeyValueMap: EXPERIMENT_ANALYTICS_TAB,
      stateValueWhenSearchParamIsInvalid: EXPERIMENT_ANALYTICS_TAB.OVERVIEW,
    }),
  });

  const [startDateTime, setStartDateTime] = useSearchParamState({
    searchParamKey: queryParamKeys.startDateTime,
    stateValueWhenSearchParamIsUnset: null,
    ...serializationOptionsForDateTypeValue,
  });

  const [endDateTime, setEndDateTime] = useSearchParamState({
    searchParamKey: queryParamKeys.endDateTime,
    stateValueWhenSearchParamIsUnset: null,
    ...serializationOptionsForDateTypeValue,
  });

  const setDateParams = ({ startDate, endDate }: DateRange) => {
    setStartDateTime(startDate);
    setEndDateTime(endDate);
  };

  const [timezone, setTimezone] = useSearchParamState({
    searchParamKey: queryParamKeys.timeZone,
    stateValueWhenSearchParamIsUnset: defaultTimeZone,
    ...serializationOptionsForStringValue,
  });

  const [selectedPerformanceOverTimeMetric, setSelectedPerformanceOverTimeMetric] =
    useSearchParamState({
      searchParamKey: queryParamKeys.selectedMetrics,
      stateValueWhenSearchParamIsUnset: defaultPerformanceOverTimeMetric,
      ...serializationOptionsForStringArrayValue,
    });

  return {
    experimentId,
    selectedTab,
    setSelectedTab,
    startDateTime,
    endDateTime,
    setDateParams,
    timezone,
    setTimezone,
    selectedPerformanceOverTimeMetric,
    setSelectedPerformanceOverTimeMetric,
  };
};
